import { FinalishApiError, type FinalishClient } from './client';
import type {
  FeedbackComment,
  FeedbackItem,
  FinalishDockSide,
  FinalishAnchor,
  FinalishWidgetOptions,
  ReviewPage,
} from './types';
import {
  describeElement,
  feedbackBelongsToPage,
  resolveFeedbackPoint,
  viewportSnapshot,
} from './anchors';
import {
  blurActivePageControl,
  elementDisplayName,
  reviewTargetFromEvent,
} from './dom';
import {
  placeFloatingSurface,
  placePin,
  rectFromDomRect,
  type SurfaceRect,
  viewportRect,
} from './geometry';
import { canonicalPageKey } from './route';

type ReviewMode = 'browse' | 'comment';
type CurrentGuestIdentity = { id: string; displayName: string };

export class FinalishWidget {
  private readonly client: FinalishClient;
  private readonly pollIntervalMs: number;
  private readonly onClose?: () => void;
  private readonly onSessionExpired?: () => void;
  private readonly onDockSideChange?: (side: FinalishDockSide) => void;
  private readonly onRouteChange?: FinalishWidgetOptions['onRouteChange'];
  private readonly startHidden: boolean;
  private host?: HTMLDivElement;
  private shadow?: ShadowRoot;
  private page?: ReviewPage;
  private items: FeedbackItem[] = [];
  private currentGuestIdentity?: CurrentGuestIdentity;
  private readonly threadComments = new Map<string, FeedbackComment[]>();
  private mode: ReviewMode = 'browse';
  private pollTimer?: number;
  private mutationObserver?: MutationObserver;
  private composer?: HTMLFormElement;
  private thread?: HTMLElement;
  private threadItemId?: string;
  private threadPoint?: { x: number; y: number };
  private threadAvoid?: SurfaceRect;
  private threadRevision = 0;
  private destroyed = false;
  private highlightedElement?: Element;
  private highlightKind: 'hover' | 'selected' = 'hover';
  private routeHref = location.href;
  private activePageKey = canonicalPageKey();
  private routeRevision = 0;
  private refreshRevision = 0;
  private routeSyncQueued = false;
  private sessionExpiryReported = false;
  private dockSide: FinalishDockSide;
  private composerPoint?: { x: number; y: number };
  private composerAvoid?: SurfaceRect;
  private originalPushState?: History['pushState'];
  private originalReplaceState?: History['replaceState'];
  private patchedPushState?: History['pushState'];
  private patchedReplaceState?: History['replaceState'];

  constructor(options: FinalishWidgetOptions) {
    this.client = options.client;
    this.pollIntervalMs = Math.max(options.pollIntervalMs ?? 5000, 2000);
    this.onClose = options.onClose;
    this.onSessionExpired = options.onSessionExpired;
    this.onDockSideChange = options.onDockSideChange;
    this.onRouteChange = options.onRouteChange;
    this.startHidden = options.startHidden ?? false;
    this.dockSide = options.dockSide ?? 'right';
  }

  async mount(): Promise<void> {
    if (this.host) return;
    this.destroyed = false;
    this.routeHref = location.href;
    this.activePageKey = canonicalPageKey();
    this.createSurface();

    try {
      this.currentGuestIdentity = guestIdentityFromBootstrap(
        await this.client.bootstrap(),
      );
      if (this.destroyed) return;
      this.routeHref = location.href;
      this.patchHistory();
      await this.syncRoute(false);
      this.startPolling();
    } catch (error) {
      this.handleAsyncError(error);
      throw error;
    }
  }

  async resume(): Promise<void> {
    this.sessionExpiryReported = false;
    this.currentGuestIdentity = guestIdentityFromBootstrap(
      await this.client.bootstrap(),
    );
    await this.syncRoute(false);
  }

  show(): void {
    if (this.host) this.host.style.visibility = 'visible';
  }

  destroy(): void {
    this.destroyed = true;
    if (this.pollTimer) window.clearInterval(this.pollTimer);
    this.mutationObserver?.disconnect();
    this.mutationObserver = undefined;
    document.removeEventListener('pointerdown', this.handlePagePointer, true);
    document.removeEventListener('pointermove', this.handlePointerMove, true);
    document.removeEventListener('pointerleave', this.handlePointerLeave, true);
    document.removeEventListener('click', this.blockPageInteraction, true);
    document.removeEventListener('auxclick', this.blockPageInteraction, true);
    document.removeEventListener('beforeinput', this.blockPageInteraction, true);
    document.removeEventListener('submit', this.blockPageInteraction, true);
    window.removeEventListener('scroll', this.handleViewportChange, true);
    window.removeEventListener('resize', this.handleViewportChange);
    window.visualViewport?.removeEventListener('resize', this.handleViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.handleViewportChange);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('popstate', this.handleRouteSignal);
    window.removeEventListener('hashchange', this.handleRouteSignal);
    this.restoreHistory();
    document.documentElement.style.cursor = '';
    this.host?.remove();
    this.host = undefined;
    this.shadow = undefined;
    this.composer = undefined;
    this.composerPoint = undefined;
    this.composerAvoid = undefined;
    this.thread = undefined;
    this.threadItemId = undefined;
    this.threadPoint = undefined;
    this.threadAvoid = undefined;
    this.threadRevision += 1;
  }

  async refresh(): Promise<void> {
    if (!this.page || this.destroyed) return;
    const pageId = this.page.id;
    const pageKey = this.activePageKey;
    const revision = this.routeRevision;
    const refresh = ++this.refreshRevision;
    let items: FeedbackItem[];
    try {
      items = await this.client.listFeedback({ pageId });
    } catch (error) {
      if (
        this.destroyed ||
        revision !== this.routeRevision ||
        pageKey !== canonicalPageKey()
      ) return;
      throw error;
    }
    if (
      this.destroyed ||
      revision !== this.routeRevision ||
      refresh !== this.refreshRevision ||
      this.page?.id !== pageId ||
      pageKey !== this.activePageKey ||
      pageKey !== canonicalPageKey()
    ) return;
    this.items = items.filter(
      (item) => item.pageId === pageId && feedbackBelongsToPage(item, pageKey),
    );
    this.renderPins();
  }

  private async syncRoute(notify: boolean): Promise<void> {
    const revision = ++this.routeRevision;
    const route = {
      href: location.href,
      title: document.title || location.hostname,
    };
    this.routeHref = route.href;
    const pageKey = canonicalPageKey(route.href);
    this.invalidateRouteSurface(pageKey);
    let page: ReviewPage;
    try {
      page = await this.client.resolvePage({
        title: route.title,
        url: route.href,
      });
    } catch (error) {
      if (
        this.destroyed ||
        revision !== this.routeRevision ||
        pageKey !== canonicalPageKey()
      ) return;
      throw error;
    }
    if (
      this.destroyed ||
      revision !== this.routeRevision ||
      pageKey !== canonicalPageKey()
    ) return;
    if (canonicalPageKey(page.normalizedUrl) !== pageKey) {
      throw new FinalishApiError(
        409,
        'page_route_mismatch',
        'Finalish вернул страницу для другого маршрута.',
      );
    }
    this.page = page;
    this.setPageAvailability(true);
    await this.refresh();
    if (
      this.destroyed ||
      revision !== this.routeRevision ||
      this.page?.id !== page.id ||
      pageKey !== canonicalPageKey()
    ) return;
    if (notify) this.onRouteChange?.({ ...route, page });
  }

  private invalidateRouteSurface(pageKey: string): void {
    this.activePageKey = pageKey;
    this.refreshRevision += 1;
    this.page = undefined;
    this.items = [];
    this.setPageAvailability(false);
    this.setMode('browse');
    this.closeComposer();
    this.closeThread();
    this.clearHighlight();
    this.renderPins();
  }

  private createSurface(): void {
    this.host = document.createElement('div');
    this.host.dataset.finalishOverlay = 'true';
    this.host.dataset.dockSide = this.dockSide;
    this.host.dataset.mode = this.mode;
    this.host.style.cssText =
      `position:fixed;inset:0;z-index:2147483000;pointer-events:none;visibility:${this.startHidden ? 'hidden' : 'visible'};`;
    this.shadow = this.host.attachShadow({ mode: 'open' });
    this.shadow.innerHTML = `<style>${STYLES}</style>
      <div class="highlight" hidden></div>
      <div class="highlight-label" hidden></div>
      <div class="pins" aria-live="polite"></div>
      <div class="dock">
        <div class="mode-hint" role="status" aria-live="polite">
          Выберите элемент на странице <kbd>Esc</kbd>
        </div>
        <div class="toolbar" role="toolbar" aria-label="Режим визуального ревью">
          <button type="button" class="toolbar-brand" data-close
            aria-label="Свернуть Finalish" data-tooltip="Свернуть Finalish">
            <svg viewBox="0 0 64 64" fill="none" focusable="false" aria-hidden="true">
              <g transform="translate(14 6)" fill="currentColor">
                <path d="M0 52V16C0 7.163 7.163 0 16 0H25V8H16C11.582 8 8 11.582 8 16H27L20 24H8V52H0Z"/>
                <path d="M31 16H36V52H28V24H24Z"/>
                <path d="M28 0H36V8H28Z"/>
              </g>
            </svg>
          </button>
          <span class="divider" aria-hidden="true"></span>
          <button type="button" class="mode is-active" data-mode="browse"
            aria-label="Режим просмотра" data-tooltip="Просмотр" aria-pressed="true">
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <path d="M4 2.75 15.6 9l-5.15 1.52-2.58 4.73L4 2.75Z"/>
            </svg>
          </button>
          <button type="button" class="mode" data-mode="comment"
            aria-label="Оставить комментарий" data-tooltip="Комментарий" aria-pressed="false">
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <path d="M4.25 3.75h11.5v9H9l-3.75 3v-3h-1v-9Z"/>
            </svg>
          </button>
          <button type="button" class="dock-side" data-dock-side
            aria-label="Переместить панель влево" data-tooltip="Переместить влево">
            <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
              <path d="M4 10h12M8 6l-4 4 4 4"/>
            </svg>
          </button>
        </div>
      </div>
      <div class="toasts" aria-live="polite"></div>`;
    this.shadow.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach(
      (button) => {
        button.addEventListener('click', () => {
          this.setMode(button.dataset.mode as ReviewMode);
        });
      },
    );
    this.shadow.querySelector<HTMLButtonElement>('[data-close]')?.addEventListener(
      'click',
      () => this.onClose?.(),
    );
    this.shadow.querySelector<HTMLButtonElement>('[data-dock-side]')?.addEventListener(
      'click',
      () => this.setDockSide(this.dockSide === 'right' ? 'left' : 'right'),
    );
    document.body.append(this.host);
    document.addEventListener('pointerdown', this.handlePagePointer, true);
    document.addEventListener('pointermove', this.handlePointerMove, true);
    document.addEventListener('pointerleave', this.handlePointerLeave, true);
    document.addEventListener('click', this.blockPageInteraction, true);
    document.addEventListener('auxclick', this.blockPageInteraction, true);
    document.addEventListener('beforeinput', this.blockPageInteraction, true);
    document.addEventListener('submit', this.blockPageInteraction, true);
    window.addEventListener('scroll', this.handleViewportChange, true);
    window.addEventListener('resize', this.handleViewportChange);
    window.visualViewport?.addEventListener('resize', this.handleViewportChange);
    window.visualViewport?.addEventListener('scroll', this.handleViewportChange);
    document.addEventListener('keydown', this.handleKeydown);
    this.mutationObserver = new MutationObserver(() => this.renderPins());
    this.mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
    });
    this.updateDockControl();
    this.setPageAvailability(false);
  }

  private setMode(mode: ReviewMode): void {
    this.mode = mode;
    if (this.host) this.host.dataset.mode = mode;
    this.shadow?.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach(
      (button) => {
        const active = button.dataset.mode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      },
    );
    document.documentElement.style.cursor =
      mode === 'comment' ? 'crosshair' : '';
    if (mode === 'comment') {
      blurActivePageControl();
    } else {
      this.closeComposer();
      this.clearHighlight();
    }
  }

  private setDockSide(side: FinalishDockSide): void {
    if (this.dockSide === side) return;
    this.dockSide = side;
    if (this.host) this.host.dataset.dockSide = side;
    this.updateDockControl();
    this.repositionComposer();
    this.repositionThread();
    this.onDockSideChange?.(side);
  }

  private updateDockControl(): void {
    const control = this.shadow?.querySelector<HTMLButtonElement>('[data-dock-side]');
    if (!control) return;
    const target = this.dockSide === 'right' ? 'влево' : 'вправо';
    control.setAttribute('aria-label', `Переместить панель ${target}`);
    control.dataset.tooltip = `Переместить ${target}`;
    control.classList.toggle('is-left', this.dockSide === 'left');
  }

  private setPageAvailability(available: boolean): void {
    const toolbar = this.shadow?.querySelector<HTMLElement>('.toolbar');
    toolbar?.setAttribute('aria-busy', String(!available));
    const comment = this.shadow?.querySelector<HTMLButtonElement>(
      '[data-mode="comment"]',
    );
    if (comment) comment.disabled = !available;
  }

  private readonly handlePagePointer = (event: PointerEvent): void => {
    if (
      this.mode !== 'comment' ||
      !this.shadow ||
      event.button !== 0 ||
      event.composedPath().includes(this.host as EventTarget)
    ) {
      return;
    }
    const target = reviewTargetFromEvent(event, this.host);
    if (!target) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.highlight(target, 'selected');
    this.openComposer(event.clientX, event.clientY, {
      x: event.pageX,
      y: event.pageY,
      selector: describeElement(target, event),
    });
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (this.mode !== 'comment' || this.composer) return;
    const target = reviewTargetFromEvent(event, this.host);
    if (!target) {
      if (this.highlightKind === 'hover') this.clearHighlight();
      return;
    }
    if (
      target === this.highlightedElement &&
      this.highlightKind === 'hover'
    ) {
      return;
    }
    this.highlight(target, 'hover');
  };

  private readonly handlePointerLeave = (): void => {
    if (this.mode === 'comment' && this.highlightKind === 'hover') {
      this.clearHighlight();
    }
  };

  private readonly blockPageInteraction = (event: Event): void => {
    if (
      this.mode !== 'comment' ||
      event.composedPath().includes(this.host as EventTarget)
    ) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private openComposer(
    clientX: number,
    clientY: number,
    anchor: { x: number; y: number; selector: FinalishAnchor },
  ): void {
    if (!this.page) {
      this.showToast('Страница ещё подключается. Попробуйте через секунду.', 'message');
      return;
    }
    this.closeComposer();
    const pageId = this.page.id;
    const pageKey = this.activePageKey;
    const routeRevision = this.routeRevision;
    const form = document.createElement('form');
    form.className = 'composer';
    form.innerHTML = `
      <div class="composer-heading">
        <label for="finalish-comment">Новый комментарий</label>
        <button type="button" class="composer-close" data-cancel
          aria-label="Закрыть форму" title="Закрыть">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 6 8 8m0-8-8 8"/></svg>
        </button>
      </div>
      <textarea id="finalish-comment" maxlength="5000" rows="3"
        placeholder="Опишите, что нужно изменить…" required></textarea>
      <div class="composer-actions">
        <span class="composer-shortcut">⌘ Enter</span>
        <button type="submit" class="primary">Отправить</button>
      </div>`;
    form.querySelector('[data-cancel]')?.addEventListener('click', () => this.closeComposer());
    const textarea = form.querySelector<HTMLTextAreaElement>('textarea');
    const submit = form.querySelector<HTMLButtonElement>('.primary');
    let submitting = false;
    const updateSubmitState = () => {
      if (submit && !submitting) submit.disabled = !textarea?.value.trim();
    };
    textarea?.addEventListener('input', updateSubmitState);
    textarea?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        form.requestSubmit();
      }
    });
    updateSubmitState();
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (
        submitting ||
        !textarea?.value.trim() ||
        !this.page ||
        this.page.id !== pageId ||
        this.activePageKey !== pageKey ||
        canonicalPageKey() !== pageKey ||
        this.routeRevision !== routeRevision ||
        !submit
      ) {
        if (canonicalPageKey() !== pageKey || this.page?.id !== pageId) {
          this.closeComposer();
          this.showToast('Маршрут изменился — выберите элемент заново.', 'message');
        }
        return;
      }
      submitting = true;
      submit.disabled = true;
      submit.textContent = 'Отправляем…';
      try {
        await this.client.createFeedback({
          pageId,
          message: textarea.value.trim(),
          selector: anchor.selector,
          position: { x: anchor.x, y: anchor.y },
          viewport: viewportSnapshot(),
        });
        this.closeComposer();
        this.setMode('browse');
        await this.refresh();
        this.showToast('Комментарий добавлен', 'success');
      } catch (error) {
        submitting = false;
        submit.disabled = false;
        submit.textContent = 'Отправить';
        this.handleAsyncError(error);
      }
    });
    this.shadow?.append(form);
    this.composer = form;
    this.composerPoint = { x: clientX, y: clientY };
    this.composerAvoid = {
      left: anchor.selector.elementRect.x,
      top: anchor.selector.elementRect.y,
      width: anchor.selector.elementRect.width,
      height: anchor.selector.elementRect.height,
    };
    this.repositionComposer();
    form.querySelector('textarea')?.focus();
  }

  private closeComposer(): void {
    this.composer?.remove();
    this.composer = undefined;
    this.composerPoint = undefined;
    this.composerAvoid = undefined;
  }

  private repositionComposer(): void {
    if (!this.composer || !this.composerPoint) return;
    const viewport = viewportRect();
    const measured = this.composer.getBoundingClientRect();
    const width = measured.width || Math.min(304, Math.max(240, viewport.width - 24));
    const height = measured.height || 208;
    const toolbar = this.shadow?.querySelector<HTMLElement>('.toolbar');
    const toolbarRect = toolbar
      ? rectFromDomRect(toolbar.getBoundingClientRect())
      : null;
    const avoid = [this.composerAvoid, toolbarRect].filter(
      (rect): rect is SurfaceRect => rect !== undefined && rect !== null,
    );
    const placement = placeFloatingSurface(
      this.composerPoint,
      { width, height },
      viewport,
      { avoid },
    );
    this.composer.style.left = `${placement.left}px`;
    this.composer.style.top = `${placement.top}px`;
  }

  private readonly renderPins = (): void => {
    const container = this.shadow?.querySelector<HTMLDivElement>('.pins');
    if (!container) return;
    if (
      !this.page ||
      canonicalPageKey() !== this.activePageKey ||
      canonicalPageKey(this.page.normalizedUrl) !== this.activePageKey
    ) {
      container.replaceChildren();
      return;
    }
    const viewport = viewportRect();
    const currentItems = this.items.filter(
      (item) =>
        item.pageId === this.page?.id &&
        feedbackBelongsToPage(item, this.activePageKey),
    );
    const pins: HTMLButtonElement[] = [];
    let openThreadVisible = false;
    currentItems.forEach((item) => {
      const resolved = resolveFeedbackPoint(item);
      // Never show a stale pixel fallback when the anchored element is gone or hidden.
      if (!resolved.found) return;
      const position = placePin(resolved, 40, viewport);
      if (!position.visible) return;
      const markerNumber = pins.length + 1;
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.feedbackId = item.id;
      button.className = `pin${item.status === 'resolved' ? ' is-resolved' : ''}`;
      button.style.left = `${position.left}px`;
      button.style.top = `${position.top}px`;
      button.setAttribute(
        'aria-label',
        `Комментарий ${markerNumber}, ${item.status === 'resolved' ? 'решён' : 'открыт'}`,
      );
      const threadOpen = this.threadItemId === item.id;
      button.setAttribute('aria-expanded', String(threadOpen));
      button.setAttribute('aria-controls', 'finalish-marker-thread');
      button.innerHTML = `<span>${item.status === 'resolved' ? '✓' : markerNumber}</span>`;
      button.addEventListener('click', () => {
        this.openThread(item, { x: resolved.x, y: resolved.y });
      });
      if (threadOpen) {
        openThreadVisible = true;
        this.threadPoint = { x: resolved.x, y: resolved.y };
        this.threadAvoid = {
          left: position.left,
          top: position.top,
          width: 40,
          height: 40,
        };
      }
      pins.push(button);
    });
    container.replaceChildren(...pins);
    if (this.thread && !openThreadVisible) this.closeThread();
    else this.repositionThread();
  };

  private openThread(item: FeedbackItem, point: { x: number; y: number }): void {
    if (!this.page) return;
    if (this.threadItemId === item.id) {
      this.closeThread();
      return;
    }
    this.closeComposer();
    this.closeThread();
    const pageId = this.page.id;
    const pageKey = this.activePageKey;
    const routeRevision = this.routeRevision;
    const threadRevision = ++this.threadRevision;
    const thread = document.createElement('section');
    thread.id = 'finalish-marker-thread';
    thread.className = 'thread';
    thread.setAttribute('role', 'dialog');
    thread.setAttribute('aria-modal', 'false');
    thread.setAttribute('aria-labelledby', 'finalish-thread-title');

    const header = document.createElement('header');
    header.className = 'thread-header';
    const heading = document.createElement('div');
    heading.className = 'thread-heading';
    const title = document.createElement('h2');
    title.id = 'finalish-thread-title';
    title.textContent = 'Обсуждение';
    const status = document.createElement('span');
    status.className = `thread-status${item.status === 'resolved' ? ' is-resolved' : ''}`;
    status.textContent = item.status === 'resolved' ? 'Решено' : 'Открыто';
    heading.append(title, status);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'thread-close';
    close.setAttribute('aria-label', 'Закрыть обсуждение');
    close.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m6 6 8 8m0-8-8 8"/></svg>';
    close.addEventListener('click', () => this.closeThread());
    header.append(heading, close);

    const body = document.createElement('div');
    body.className = 'thread-body';
    body.append(this.createMessageCard(item.message, item, true));
    const replies = document.createElement('div');
    replies.className = 'thread-replies';
    replies.setAttribute('aria-live', 'polite');
    const loading = document.createElement('div');
    loading.className = 'thread-loading';
    loading.textContent = 'Загружаем ответы…';
    replies.append(loading);
    body.append(replies);

    const form = document.createElement('form');
    form.className = 'thread-reply';
    const label = document.createElement('label');
    label.className = 'sr-only';
    label.htmlFor = 'finalish-thread-reply';
    label.textContent = 'Ответить в обсуждении';
    const textarea = document.createElement('textarea');
    textarea.id = 'finalish-thread-reply';
    textarea.rows = 2;
    textarea.maxLength = 5000;
    textarea.placeholder = 'Написать ответ…';
    const actions = document.createElement('div');
    actions.className = 'thread-actions';
    const shortcut = document.createElement('span');
    shortcut.textContent = '⌘ Enter';
    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'primary';
    submit.textContent = 'Ответить';
    submit.disabled = true;
    actions.append(shortcut, submit);
    form.append(label, textarea, actions);
    thread.append(header, body, form);
    this.shadow?.append(thread);
    this.thread = thread;
    this.threadItemId = item.id;
    this.threadPoint = point;
    this.threadAvoid = {
      left: point.x - 20,
      top: point.y - 20,
      width: 40,
      height: 40,
    };
    this.shadow?.querySelectorAll<HTMLButtonElement>('.pin').forEach((pin) => {
      pin.setAttribute(
        'aria-expanded',
        String(pin.dataset.feedbackId === item.id),
      );
    });
    this.repositionThread();

    let comments = this.threadComments.get(item.id) ?? [];
    let submitting = false;
    const active = () =>
      !this.destroyed &&
      this.thread === thread &&
      this.threadRevision === threadRevision &&
      this.routeRevision === routeRevision &&
      this.page?.id === pageId &&
      this.activePageKey === pageKey &&
      canonicalPageKey() === pageKey;
    const updateSubmit = () => {
      submit.disabled = submitting || !textarea.value.trim();
    };
    if (comments.length > 0) {
      this.renderThreadReplies(replies, comments);
      this.repositionThread();
    }
    textarea.addEventListener('input', updateSubmit);
    textarea.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        form.requestSubmit();
      }
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const value = textarea.value.trim();
      if (!value || submitting || !active()) return;
      submitting = true;
      updateSubmit();
      submit.textContent = 'Отправляем…';
      this.setThreadError(thread, '');
      try {
        const comment = await this.client.addComment(item.id, pageId, value);
        if (!active()) return;
        comments = mergeFeedbackComments(comments, [comment]);
        this.threadComments.set(item.id, comments);
        this.renderThreadReplies(replies, comments);
        textarea.value = '';
        submitting = false;
        submit.textContent = 'Ответить';
        updateSubmit();
        this.repositionThread();
        textarea.focus();
      } catch (error) {
        if (!active()) return;
        submitting = false;
        submit.textContent = 'Ответить';
        updateSubmit();
        this.setThreadError(thread, errorMessage(error));
      }
    });

    void this.client.listComments(item.id, pageId).then(
      (loaded) => {
        if (!active()) return;
        comments = mergeFeedbackComments(
          loaded.filter((comment) => !comment.isInternal),
          comments,
        );
        this.threadComments.set(item.id, comments);
        this.renderThreadReplies(replies, comments);
        this.repositionThread();
      },
      (error) => {
        if (!active()) return;
        replies.replaceChildren();
        this.setThreadError(thread, errorMessage(error));
        this.repositionThread();
      },
    );
  }

  private createMessageCard(
    message: string,
    author: Pick<
      FeedbackItem | FeedbackComment,
      'authorType' | 'authorId' | 'authorDisplayName' | 'createdAt'
    >,
    original = false,
  ): HTMLElement {
    const card = document.createElement('article');
    card.className = `thread-message${original ? ' is-original' : ''}`;
    const meta = document.createElement('div');
    meta.className = 'thread-meta';
    const avatar = document.createElement('span');
    avatar.className = 'thread-avatar';
    avatar.textContent = authorInitial(author, this.currentGuestIdentity);
    const name = document.createElement('strong');
    name.textContent = authorName(author, this.currentGuestIdentity);
    const time = document.createElement('time');
    time.dateTime = author.createdAt;
    time.textContent = formatTimestamp(author.createdAt);
    meta.append(avatar, name, time);
    const content = document.createElement('p');
    content.textContent = message;
    card.append(meta, content);
    return card;
  }

  private renderThreadReplies(
    container: HTMLElement,
    comments: FeedbackComment[],
  ): void {
    if (comments.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'thread-empty';
      empty.textContent = 'Ответов пока нет. Начните обсуждение.';
      container.replaceChildren(empty);
      return;
    }
    container.replaceChildren(
      ...comments.map((comment) =>
        this.createMessageCard(comment.body, comment),
      ),
    );
  }

  private setThreadError(thread: HTMLElement, message: string): void {
    thread.querySelector('.thread-error')?.remove();
    if (!message) return;
    const error = document.createElement('p');
    error.className = 'thread-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    thread.querySelector('.thread-reply')?.prepend(error);
  }

  private closeThread(): void {
    if (!this.thread && !this.threadItemId) return;
    this.threadRevision += 1;
    this.thread?.remove();
    this.thread = undefined;
    this.threadItemId = undefined;
    this.threadPoint = undefined;
    this.threadAvoid = undefined;
    this.shadow?.querySelectorAll<HTMLButtonElement>('.pin').forEach((pin) => {
      pin.setAttribute('aria-expanded', 'false');
    });
  }

  private repositionThread(): void {
    if (!this.thread || !this.threadPoint) return;
    const viewport = viewportRect();
    const measured = this.thread.getBoundingClientRect();
    const width = measured.width || Math.min(356, Math.max(280, viewport.width - 24));
    const height = measured.height || Math.min(440, viewport.height - 24);
    const toolbar = this.shadow?.querySelector<HTMLElement>('.toolbar');
    const toolbarRect = toolbar
      ? rectFromDomRect(toolbar.getBoundingClientRect())
      : null;
    const avoid = [this.threadAvoid, toolbarRect].filter(
      (rect): rect is SurfaceRect => rect !== undefined && rect !== null,
    );
    const placement = placeFloatingSurface(
      this.threadPoint,
      { width, height },
      viewport,
      { gap: 8, avoid },
    );
    this.thread.style.left = `${placement.left}px`;
    this.thread.style.top = `${placement.top}px`;
  }

  private readonly handleViewportChange = (): void => {
    this.renderPins();
    this.repositionComposer();
    this.repositionThread();
    if (this.highlightedElement?.isConnected) {
      this.highlight(this.highlightedElement, this.highlightKind);
    } else {
      this.clearHighlight();
    }
  };

  private highlight(
    element: Element,
    kind: 'hover' | 'selected',
  ): void {
    this.highlightedElement = element;
    this.highlightKind = kind;
    const highlight = this.shadow?.querySelector<HTMLDivElement>('.highlight');
    const label =
      this.shadow?.querySelector<HTMLDivElement>('.highlight-label');
    if (!highlight || !label) return;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      this.clearHighlight();
      return;
    }
    highlight.hidden = false;
    label.hidden = false;
    highlight.classList.toggle('is-selected', kind === 'selected');
    highlight.style.left = `${rect.left}px`;
    highlight.style.top = `${rect.top}px`;
    highlight.style.width = `${rect.width}px`;
    highlight.style.height = `${rect.height}px`;
    label.textContent = elementDisplayName(element);
    const viewport = viewportRect();
    const labelWidth = label.getBoundingClientRect().width || 220;
    const labelHeight = label.getBoundingClientRect().height || 24;
    label.style.left = `${clamp(
      rect.left,
      viewport.left + 8,
      Math.max(viewport.left + 8, viewport.left + viewport.width - labelWidth - 8),
    )}px`;
    const preferredTop = rect.top - labelHeight - 6 >= viewport.top + 8
      ? rect.top - labelHeight - 6
      : rect.bottom + 6;
    label.style.top = `${clamp(
      preferredTop,
      viewport.top + 8,
      Math.max(viewport.top + 8, viewport.top + viewport.height - labelHeight - 8),
    )}px`;
  }

  private clearHighlight(): void {
    this.highlightedElement = undefined;
    const highlight = this.shadow?.querySelector<HTMLDivElement>('.highlight');
    const label =
      this.shadow?.querySelector<HTMLDivElement>('.highlight-label');
    if (highlight) highlight.hidden = true;
    if (label) label.hidden = true;
  }

  private startPolling(): void {
    this.pollTimer = window.setInterval(() => {
      if (this.routeHref !== location.href) {
        this.handleRouteSignal();
        return;
      }
      void this.refresh().catch((error) => this.handleAsyncError(error));
    }, this.pollIntervalMs);
  }

  private patchHistory(): void {
    if (this.patchedPushState || this.patchedReplaceState) return;
    this.originalPushState = history.pushState;
    this.originalReplaceState = history.replaceState;
    this.patchedPushState = ((data, unused, url) => {
      const result = this.originalPushState?.call(history, data, unused, url);
      this.handleRouteSignal();
      return result;
    }) as History['pushState'];
    this.patchedReplaceState = ((data, unused, url) => {
      const result = this.originalReplaceState?.call(history, data, unused, url);
      this.handleRouteSignal();
      return result;
    }) as History['replaceState'];
    history.pushState = this.patchedPushState;
    history.replaceState = this.patchedReplaceState;
    window.addEventListener('popstate', this.handleRouteSignal);
    window.addEventListener('hashchange', this.handleRouteSignal);
  }

  private restoreHistory(): void {
    if (this.originalPushState && history.pushState === this.patchedPushState) {
      history.pushState = this.originalPushState;
    }
    if (
      this.originalReplaceState &&
      history.replaceState === this.patchedReplaceState
    ) {
      history.replaceState = this.originalReplaceState;
    }
    this.patchedPushState = undefined;
    this.patchedReplaceState = undefined;
  }

  private readonly handleRouteSignal = (): void => {
    if (this.destroyed || this.routeHref === location.href) return;
    this.routeHref = location.href;
    this.routeRevision += 1;
    this.invalidateRouteSurface(canonicalPageKey());
    if (this.routeSyncQueued) return;
    this.routeSyncQueued = true;
    queueMicrotask(() => {
      this.routeSyncQueued = false;
      if (this.destroyed) return;
      void this.syncRoute(true).catch((error) => this.handleAsyncError(error));
    });
  };

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      if (this.thread) this.closeThread();
      else if (this.composer) this.closeComposer();
      else this.setMode('browse');
    }
  };

  private showToast(
    message: string,
    kind: 'success' | 'error' | 'message',
  ): void {
    const region = this.shadow?.querySelector('.toasts');
    if (!region) return;
    const toast = document.createElement('div');
    toast.className = `toast ${kind}`;
    toast.textContent = message;
    region.append(toast);
    window.setTimeout(() => toast.remove(), kind === 'message' ? 5000 : 3200);
  }

  private handleAsyncError(error: unknown): void {
    if (error instanceof FinalishApiError && error.status === 401) {
      if (!this.sessionExpiryReported) {
        this.sessionExpiryReported = true;
        this.onSessionExpired?.();
        this.showToast('Сессия ревью обновляется…', 'message');
      }
      return;
    }
    this.showToast(errorMessage(error), 'error');
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Не удалось выполнить действие. Попробуйте ещё раз.';
}

function authorName(
  author: Pick<
    FeedbackItem | FeedbackComment,
    'authorType' | 'authorId' | 'authorDisplayName'
  >,
  currentGuestIdentity?: CurrentGuestIdentity,
): string {
  return author.authorDisplayName?.trim() ||
    (author.authorType === 'guest' &&
    author.authorId === currentGuestIdentity?.id
      ? currentGuestIdentity.displayName
      : '') ||
    (author.authorType === 'guest' ? 'Гость' : 'Участник команды');
}

function authorInitial(
  author: Pick<
    FeedbackItem | FeedbackComment,
    'authorType' | 'authorId' | 'authorDisplayName'
  >,
  currentGuestIdentity?: CurrentGuestIdentity,
): string {
  return authorName(author, currentGuestIdentity)
    .slice(0, 1)
    .toLocaleUpperCase();
}

function guestIdentityFromBootstrap(value: unknown): CurrentGuestIdentity | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const bootstrap = value as Record<string, unknown>;
  const identity = bootstrap.identity;
  if (
    typeof bootstrap.guestIdentityId !== 'string' ||
    typeof identity !== 'object' ||
    identity === null
  ) {
    return undefined;
  }
  const displayName = (identity as Record<string, unknown>).displayName;
  if (typeof displayName !== 'string' || !displayName.trim()) return undefined;
  return {
    id: bootstrap.guestIdentityId,
    displayName: displayName.trim(),
  };
}

function mergeFeedbackComments(
  ...groups: FeedbackComment[][]
): FeedbackComment[] {
  const merged: FeedbackComment[] = [];
  const indexes = new Map<string, number>();
  for (const group of groups) {
    for (const comment of group) {
      const existingIndex = indexes.get(comment.id);
      if (existingIndex === undefined) {
        indexes.set(comment.id, merged.length);
        merged.push(comment);
        continue;
      }
      const existing = merged[existingIndex];
      merged[existingIndex] = {
        ...existing,
        ...comment,
        authorDisplayName:
          comment.authorDisplayName ?? existing.authorDisplayName,
      };
    }
  }
  return merged;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

const STYLES = `
  :host{--primary:#5417ee;--primary-dark:#4513c4;--primary-soft:#f3efff;
    --ink:#211a37;--text:#352e49;--muted:#766f82;--surface:#fff;
    --surface-hover:#f6f5f8;--border:#e2e1ea;--border-strong:#cbc8d6;
    --danger:#c83d55;font-family:"Onest",ui-sans-serif,system-ui,sans-serif;
    color:var(--text);font-synthesis:none}
  *{box-sizing:border-box}button,textarea{font:inherit}.dock{position:fixed;right:max(12px,env(safe-area-inset-right));
    bottom:max(12px,env(safe-area-inset-bottom));display:grid;justify-items:end;gap:8px;
    pointer-events:none}:host([data-dock-side="left"]) .dock{right:auto;
    left:max(12px,env(safe-area-inset-left));justify-items:start}
  .toolbar{height:44px;display:flex;align-items:center;gap:2px;padding:4px;
    border:1px solid var(--border);border-radius:9px;background:var(--surface);
    box-shadow:0 2px 8px rgb(33 26 55 / 14%);pointer-events:auto}
  .toolbar button{position:relative;width:34px;height:34px;display:grid;place-items:center;
    flex:0 0 auto;padding:0;border:0;border-radius:7px;background:transparent;color:var(--muted);
    cursor:pointer;transition:color 120ms ease,background-color 120ms ease}
  .toolbar button:hover{color:var(--ink);background:var(--surface-hover)}
  .toolbar-brand{background:var(--primary)!important;color:#fff!important}
  .toolbar-brand:hover{background:var(--primary-dark)!important}.toolbar-brand svg{width:25px;height:25px}
  .divider{width:1px;height:24px;margin:0 2px;background:var(--border)}
  .mode svg,.dock-side svg,.composer-close svg{width:18px;height:18px;fill:none;stroke:currentColor;
    stroke-width:1.7;stroke-linecap:round;stroke-linejoin:round}
  .mode.is-active{background:var(--primary-soft)!important;color:var(--primary)!important}
  .mode[data-mode="comment"].is-active{background:var(--primary)!important;color:#fff!important}
  .dock-side.is-left svg{transform:scaleX(-1)}.toolbar button:disabled{cursor:wait;opacity:.38}
  [data-tooltip]::after{position:absolute;right:0;bottom:calc(100% + 9px);width:max-content;
    max-width:180px;padding:5px 7px;border-radius:5px;background:var(--ink);color:#fff;content:attr(data-tooltip);
    font:500 11px/15px "Onest",ui-sans-serif,system-ui,sans-serif;white-space:nowrap;
    opacity:0;visibility:hidden;translate:0 2px;transition:opacity 100ms ease,translate 100ms ease;
    pointer-events:none}:host([data-dock-side="left"]) [data-tooltip]::after{right:auto;left:0}
  [data-tooltip]:hover::after,[data-tooltip]:focus-visible::after{opacity:1;visibility:visible;translate:0 0}
  .mode-hint{display:none;align-items:center;gap:10px;max-width:min(300px,calc(100vw - 24px));
    padding:7px 9px;border:1px solid var(--border);border-radius:7px;background:#fff;color:var(--text);
    box-shadow:0 2px 8px rgb(33 26 55 / 12%);font:500 12px/18px "Onest",ui-sans-serif,system-ui,sans-serif;
    pointer-events:none}:host([data-mode="comment"]) .mode-hint{display:flex}.mode-hint kbd{padding-left:9px;
    border-left:1px solid var(--border);color:var(--muted);font:500 10px/16px ui-monospace,SFMono-Regular,monospace}
  button:focus-visible,textarea:focus-visible{outline:2px solid var(--primary);outline-offset:2px}
  .highlight{position:fixed;border:2px solid var(--primary);border-radius:3px;
    background:rgb(84 23 238 / 7%);box-shadow:0 0 0 1px rgb(255 255 255 / 94%);
    pointer-events:none;transition:left 70ms ease,top 70ms ease,width 70ms ease,height 70ms ease}
  .highlight.is-selected{background:rgb(84 23 238 / 12%);box-shadow:0 0 0 1px #fff,0 0 0 3px rgb(84 23 238 / 20%)}
  .highlight-label{position:fixed;max-width:min(360px,calc(100vw - 16px));height:24px;
    overflow:hidden;padding:3px 7px;border-radius:4px;background:var(--ink);color:#fff;
    font:600 12px/18px "Onest",ui-sans-serif,system-ui,sans-serif;text-overflow:ellipsis;
    white-space:nowrap;pointer-events:none}.pins{position:fixed;inset:0;pointer-events:none}
  .pin{position:absolute;width:40px;height:40px;padding:7px;border:0;background:transparent;
    pointer-events:auto;cursor:pointer}.pin span{display:grid;place-items:center;width:26px;height:26px;
    border:2px solid var(--primary);border-radius:50%;background:#fff;color:var(--primary);
    font:600 11px/1 "Onest",ui-sans-serif,system-ui,sans-serif;box-shadow:0 2px 7px rgb(33 26 55 / 20%)}
  .pin:hover span{background:var(--primary-soft)}.pin.is-resolved span{border-color:#898297;
    background:#898297;color:#fff}.pin.is-lost span{border-style:dashed}
  .thread{position:fixed;display:flex;flex-direction:column;width:min(356px,calc(100vw - 24px));
    max-height:calc(100dvh - 24px);overflow:hidden;border:1px solid var(--border);
    border-radius:10px;background:var(--surface);box-shadow:0 8px 28px rgb(33 26 55 / 18%);
    pointer-events:auto}.thread-header{display:flex;align-items:center;justify-content:space-between;
    min-height:48px;padding:10px 10px 10px 14px;border-bottom:1px solid var(--border)}
  .thread-heading{display:flex;align-items:center;gap:8px;min-width:0}.thread-heading h2{margin:0;
    color:var(--ink);font:650 14px/20px "Onest",ui-sans-serif,system-ui,sans-serif}
  .thread-status{padding:2px 6px;border:1px solid #bcdac8;border-radius:5px;background:#f0faf4;
    color:#167443;font:600 10px/15px "Onest",ui-sans-serif,system-ui,sans-serif}
  .thread-status.is-resolved{border-color:var(--border);background:var(--surface-hover);color:var(--muted)}
  .thread-close{width:28px;height:28px;display:grid;place-items:center;flex:0 0 auto;padding:0;
    border:0;border-radius:6px;background:transparent;color:var(--muted);cursor:pointer}
  .thread-close:hover{background:var(--surface-hover);color:var(--ink)}.thread-close svg{width:18px;
    height:18px;fill:none;stroke:currentColor;stroke-width:1.7;stroke-linecap:round}
  .thread-body{min-height:0;overflow:auto;padding:12px 14px}.thread-message{padding:11px 0;
    border-top:1px solid var(--border)}.thread-message:first-child{border-top:0}.thread-message.is-original{
    margin-bottom:4px;padding:11px 12px;border:1px solid #ded6fb;border-radius:8px;background:#faf8ff}
  .thread-meta{display:grid;grid-template-columns:24px minmax(0,1fr) auto;align-items:center;gap:7px}
  .thread-avatar{display:grid;place-items:center;width:24px;height:24px;border-radius:6px;
    background:var(--primary-soft);color:var(--primary);font:700 10px/1 "Onest",ui-sans-serif,system-ui,sans-serif}
  .thread-meta strong{overflow:hidden;color:var(--ink);font:600 11px/16px "Onest",ui-sans-serif,
    system-ui,sans-serif;text-overflow:ellipsis;white-space:nowrap}.thread-meta time{color:var(--muted);
    font:500 10px/15px "Onest",ui-sans-serif,system-ui,sans-serif;white-space:nowrap}
  .thread-message p{margin:8px 0 0;color:var(--text);font:400 13px/19px "Onest",ui-sans-serif,
    system-ui,sans-serif;white-space:pre-wrap;overflow-wrap:anywhere}.thread-loading,.thread-empty{margin:0;
    padding:12px 2px 3px;color:var(--muted);font:400 12px/18px "Onest",ui-sans-serif,system-ui,sans-serif}
  .thread-reply{flex:0 0 auto;padding:10px 14px 12px;border-top:1px solid var(--border);
    background:#fff}.thread-reply textarea{min-height:58px;max-height:120px;resize:vertical}
  .thread-actions{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:8px}
  .thread-actions span{color:var(--muted);font:500 10px/16px ui-monospace,SFMono-Regular,monospace}
  .thread-actions button{height:30px;padding:0 10px;border-radius:7px;
    font:600 11px/1 "Onest",ui-sans-serif,system-ui,sans-serif;cursor:pointer}
  .thread-error{margin:0 0 8px;color:var(--danger);font:500 11px/16px "Onest",ui-sans-serif,
    system-ui,sans-serif}.sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;
    clip:rect(0,0,0,0);white-space:nowrap;border:0}
  .composer{position:fixed;width:min(304px,calc(100vw - 24px));max-height:calc(100dvh - 24px);
    overflow:auto;padding:14px;border:1px solid var(--border);border-radius:10px;background:var(--surface);
    box-shadow:0 3px 8px rgb(33 26 55 / 16%);pointer-events:auto}
  .composer-heading{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px}
  .composer label{display:block;margin:0;color:var(--ink);font:600 13px/18px "Onest",ui-sans-serif,system-ui,sans-serif}
  .composer-close{width:28px;height:28px;display:grid;place-items:center;flex:0 0 auto;padding:0;
    border:0;border-radius:6px;background:transparent;color:var(--muted);cursor:pointer}
  .composer-close:hover{background:var(--surface-hover);color:var(--ink)}
  textarea{display:block;width:100%;min-height:82px;max-height:180px;resize:vertical;padding:9px 10px;
    border:1px solid var(--border-strong);border-radius:7px;color:var(--text);background:#fff;
    font:400 13px/19px "Onest",ui-sans-serif,system-ui,sans-serif}
  textarea::placeholder{color:#9891a4}textarea:hover{border-color:#aaa5b7}
  textarea:focus{border-color:var(--primary);outline:2px solid rgb(84 23 238 / 15%)}
  .composer-actions{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:10px}
  .composer-shortcut{color:var(--muted);font:500 10px/16px ui-monospace,SFMono-Regular,monospace}
  .composer-actions button{height:32px;padding:0 11px;border-radius:7px;
    font:600 12px/1 "Onest",ui-sans-serif,system-ui,sans-serif;cursor:pointer}
  .primary{border:1px solid var(--primary);background:var(--primary);color:#fff}
  .primary:hover{background:var(--primary-dark)}button:disabled{cursor:not-allowed;opacity:.5}
  .toasts{position:fixed;right:max(12px,env(safe-area-inset-right));
    bottom:calc(max(12px,env(safe-area-inset-bottom)) + 52px);display:grid;gap:7px;
    width:min(304px,calc(100vw - 24px));pointer-events:none}:host([data-dock-side="left"]) .toasts{
    right:auto;left:max(12px,env(safe-area-inset-left))}.toast{padding:9px 10px;border:1px solid var(--border);
    border-radius:7px;background:#fff;color:var(--text);box-shadow:0 2px 8px rgb(33 26 55 / 14%);
    font:400 13px/19px "Onest",ui-sans-serif,system-ui,sans-serif}.toast.error{border-color:var(--danger)}
  .toast.success{border-color:#16834a}.toast.message{white-space:pre-wrap}
  @media(max-width:560px){.dock{right:max(10px,env(safe-area-inset-right));
      bottom:max(10px,env(safe-area-inset-bottom))}:host([data-dock-side="left"]) .dock{
      left:max(10px,env(safe-area-inset-left))}.toolbar{height:42px;padding:3px}.composer{
      width:calc(100vw - 20px);max-height:calc(100dvh - 70px)}.thread{width:calc(100vw - 20px);
      max-height:calc(100dvh - 20px)}.thread-actions span,.composer-shortcut{display:none}
    .toasts{right:10px;bottom:60px;width:calc(100vw - 20px)}:host([data-dock-side="left"]) .toasts{
      left:10px}.mode-hint{font-size:11px}}
  @media(max-height:420px){.mode-hint{display:none!important}.composer,.thread{max-height:calc(100dvh - 16px)}}
  @media(hover:none){[data-tooltip]::after{display:none}}
  @media(prefers-reduced-motion:reduce){*{transition:none!important}}
  @media(forced-colors:active){.toolbar,.composer,.thread,.toast,.mode-hint{border:1px solid ButtonText}}
`;
